import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, FileText, Trash2, BarChart3, Users, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const statusMap = {
  setup: { label: "Configuração", cls: "bg-blue-100 text-blue-800 border-blue-200" },
  uploading: { label: "Upload", cls: "bg-amber-100 text-amber-800 border-amber-200" },
  correcting: { label: "A corrigir", cls: "bg-orange-100 text-orange-800 border-orange-200" },
  completed: { label: "Completo", cls: "bg-green-100 text-green-800 border-green-200" },
};

export default function DashboardPage() {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => { loadSessions(); }, []);

  const loadSessions = async () => {
    try {
      const res = await api.get("/sessions");
      setSessions(res.data);
    } catch {
      toast.error("Erro ao carregar testes");
    } finally {
      setLoading(false);
    }
  };

  const deleteSession = async (id) => {
    try {
      await api.delete(`/sessions/${id}`);
      setSessions((s) => s.filter((x) => x.id !== id));
      toast.success("Teste eliminado");
    } catch {
      toast.error("Erro ao eliminar");
    }
  };

  const getLink = (s) => {
    if (s.status === "setup" && s.question_count === 0) return `/tests/new?edit=${s.id}`;
    if (s.student_count === 0) return `/tests/${s.id}/upload`;
    if (s.corrected_count > 0) return `/tests/${s.id}/correction`;
    return `/tests/${s.id}/upload`;
  };

  const totalStudents = sessions.reduce((a, s) => a + (s.student_count || 0), 0);
  const completedCount = sessions.filter((s) => s.corrected_count > 0).length;

  return (
    <div className="space-y-8 animate-fade-in" data-testid="dashboard-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading font-bold text-3xl sm:text-4xl text-foreground tracking-tight">Painel</h1>
          <p className="text-muted-foreground mt-1 text-sm">Gerir correções de testes</p>
        </div>
        <Button onClick={() => navigate("/tests/new")} className="active:scale-95 transition-transform" data-testid="new-test-btn">
          <Plus className="h-4 w-4 mr-2" />
          Novo Teste
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        <Card className="hover:shadow-md transition-shadow hover:-translate-y-0.5 transition-transform duration-200" data-testid="stat-tests">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-primary/10"><FileText className="h-6 w-6 text-primary" /></div>
              <div>
                <p className="text-2xl font-heading font-bold">{sessions.length}</p>
                <p className="text-sm text-muted-foreground">Testes Criados</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="hover:shadow-md transition-shadow hover:-translate-y-0.5 transition-transform duration-200" data-testid="stat-students">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-amber-50"><Users className="h-6 w-6 text-amber-600" /></div>
              <div>
                <p className="text-2xl font-heading font-bold">{totalStudents}</p>
                <p className="text-sm text-muted-foreground">Alunos Avaliados</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="hover:shadow-md transition-shadow hover:-translate-y-0.5 transition-transform duration-200" data-testid="stat-completed">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-green-50"><CheckCircle2 className="h-6 w-6 text-green-600" /></div>
              <div>
                <p className="text-2xl font-heading font-bold">{completedCount}</p>
                <p className="text-sm text-muted-foreground">Testes Corrigidos</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Sessions List */}
      {loading ? (
        <div className="flex justify-center py-16">
          <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
        </div>
      ) : sessions.length === 0 ? (
        <Card className="text-center py-16" data-testid="empty-state">
          <CardContent>
            <img
              src="https://images.pexels.com/photos/301943/pexels-photo-301943.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=200&w=300"
              alt="Empty"
              className="mx-auto mb-6 rounded-xl opacity-80 h-40 object-cover"
            />
            <h3 className="font-heading font-semibold text-lg mb-2">Nenhum teste criado</h3>
            <p className="text-muted-foreground mb-6 text-sm">Comece por criar o seu primeiro teste</p>
            <Button onClick={() => navigate("/tests/new")} data-testid="empty-new-test">
              <Plus className="h-4 w-4 mr-2" />
              Criar Primeiro Teste
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          <h2 className="font-heading font-semibold text-lg">Testes Recentes</h2>
          {sessions.map((session, i) => (
            <Card
              key={session.id}
              className="hover:shadow-md transition-all group animate-fade-in"
              style={{ animationDelay: `${i * 50}ms` }}
              data-testid={`session-card-${session.id}`}
            >
              <CardContent className="py-4 flex items-center justify-between gap-4">
                <Link to={getLink(session)} className="flex-1 flex items-center gap-4 min-w-0">
                  <div className="p-2.5 rounded-lg bg-primary/5 shrink-0">
                    <FileText className="h-5 w-5 text-primary" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-medium truncate">{session.title}</h3>
                    <p className="text-sm text-muted-foreground">
                      {session.subject} &middot; {session.question_count} questões &middot; {session.student_count} alunos
                    </p>
                  </div>
                </Link>
                <div className="flex items-center gap-2 shrink-0">
                  <Badge className={statusMap[session.status]?.cls || "bg-gray-100"} data-testid={`status-${session.id}`}>
                    {statusMap[session.status]?.label || session.status}
                  </Badge>
                  {session.corrected_count > 0 && (
                    <Link to={`/tests/${session.id}/evaluation`}>
                      <Button variant="outline" size="sm" data-testid={`eval-btn-${session.id}`}>
                        <BarChart3 className="h-4 w-4" />
                      </Button>
                    </Link>
                  )}
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="ghost" size="sm" className="opacity-0 group-hover:opacity-100 transition-opacity text-destructive" data-testid={`delete-btn-${session.id}`}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Eliminar teste?</AlertDialogTitle>
                        <AlertDialogDescription>Esta ação é irreversível. Todos os dados serão eliminados.</AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancelar</AlertDialogCancel>
                        <AlertDialogAction onClick={() => deleteSession(session.id)} data-testid={`confirm-delete-${session.id}`}>
                          Eliminar
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
